import {
	Backdrop,
	Box, Button,
	Checkbox,
	CircularProgress,
	Dialog,
	FormControlLabel,
	IconButton,
	InputBase,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Theme,
	Typography
} from "@mui/material";
import useEmotionStyles from "@styles/shared/useEmotionStyles";
import { MarkFeedbackListStyle } from "@styles/assignments/MarkFeedbackListStyle";

import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";

import { ReactComponent as CheckCircleIcon } from '@common/images/CheckCircleIcon.svg';
import { ReactComponent as CheckCircleIconOff } from '@common/images/CheckCircleIconOff.svg';
import { ReactComponent as EmptyUserBg } from '@common/images/EmptyUserBg.svg';
import { ReactComponent as SearchNameIcon } from '@common/images/SearchNameIcon.svg';
import { ReactComponent as TableListOrder } from '@common/images/TableListOrder.svg';

import { observer } from "mobx-react";

// import 'dayjs/locale/ko';
import { useTranslation } from "react-i18next";

import useNaviCustom from "@hooks/useNaviCustom";
import MarkFeedback from "./MarkFeedBackPopUp/MarkFeedback";
import { Row } from "./Row";

import React, { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useStores } from "@hooks/useStores";
import { ProjectStateType } from "@repositories/model/support/ProjectStateType";
import { DetailPageType } from "@stores/support/DetailPageType";
import { SortingHintColumn } from "@stores/support/SortingHintColumn";


type Props = {
}

function MarkFeedbackList(props: Props) {
	const [searchParams] = useSearchParams();
	const params = useParams();

	const { projectSubmissionAnswerStore, navigateStore, projectStore, messageStore } = useStores();
	const classes = useEmotionStyles( MarkFeedbackListStyle );
	const i18next = useTranslation();
	const {
		projectCode,
		answerScoreList,
		answerScoreListConditionsToSearch
	} = projectSubmissionAnswerStore;

	const [pageChangingCount, setPageChangingCount] = React.useState(0);
	const [searchKeyword, setSearchKeyword] = React.useState("");
	const [sortStateMap, setSortStateMap] = React.useState({
		[SortingHintColumn.SCORED_DATE_TIME]: { sort: false },
	});
	const [checkedOnlyScored, setCheckedOnlyScored] = React.useState(false);
	const [openMarkDiolog, setOpenMarkDiolog] = React.useState(false);
	const [markSubmissionCode, setMarkSubmissionCode] = React.useState("");

	const [needInterval, setNeedInterval] = React.useState(false);
	const onTheAiIntervalId = React.useRef(null);

	const naviCustom = useNaviCustom();

	const [isBulkScoreLoading, setIsBulkScoreLoading] = useState(false);

	React.useEffect(() => {
		navigateStore.changeNavigateRootPage(false);
		navigateStore.changeNavigateDetailPage(DetailPageType.PROJECT);
		navigateStore.changeSideBarStepNum(4);
		projectStore.downloadProjectInfoAndStore(params.projectCode);

		return () => {
			navigateStore.changeNavigateDetailPage(null);
			projectSubmissionAnswerStore.init();
			projectStore.initProject();
		}
	}, []);

	React.useEffect(() => {
		const projectCode = params.projectCode;
		if (!projectCode || projectSubmissionAnswerStore.projectCode === projectCode) return;

		projectSubmissionAnswerStore.setProjectCode(projectCode);
		projectSubmissionAnswerStore.changeAnswerScoreListConditionsToSearchStateType(ProjectStateType.COMPARED);
		projectSubmissionAnswerStore.getAnswerScoreList(projectCode);
	}, [params]);

	React.useEffect(() => {
		const submissionCode = searchParams.get('submissionCode');
		if (submissionCode) {
			handleOpenMarkDialog(submissionCode);
		}
	}, [searchParams]);

	React.useEffect(() => {
		if (pageChangingCount === 0) {
			setPageChangingCount(prev => prev + 1);
			return;
		}
		initOnTheAiCheckInterval();
		projectSubmissionAnswerStore.getAnswerScoreList(projectCode);
	}, [answerScoreListConditionsToSearch.page]);

	React.useEffect(() => {
		if (answerScoreList.length > 0) {
			const result = answerScoreList.some(answer => answer.onTheAi > 0);
			setNeedInterval(result);
		}
	}, [answerScoreList])

	React.useEffect(() => {
		if (needInterval) {
			// 1. 돌고 있었는지 체크.
			if (onTheAiIntervalId.current) {
				// 2. 돌고 있었으면 인터벌을 없애고, 새롭게 결과를 가져오기
				clearInterval(onTheAiIntervalId.current);
			} else {
				// 3. 안돌고 있었으면 -> 인터벌 시작
				onTheAiIntervalId.current = setInterval(() => projectSubmissionAnswerStore.getAnswerScoreList(projectCode), 5000);
			}
		} else {
			// 이곳은 모든 채점 결과가 완료 되었을 때 이다.
			initOnTheAiCheckInterval();
		}
	}, [needInterval])

	const initOnTheAiCheckInterval = () => {
		if (onTheAiIntervalId.current) {
			clearInterval(onTheAiIntervalId.current);
		}
		setNeedInterval(false);
	}

	const currentAnswerScoreListConditionsToSearch = React.useMemo(() => {
		const list = answerScoreList;
		const condition = answerScoreListConditionsToSearch;

		return list?.length > 0
			? condition?.totalCount > condition.rowsPerPage
				? Math.ceil(condition?.totalCount / condition.rowsPerPage)
				: 1
			: 0
	}, [answerScoreList, answerScoreListConditionsToSearch]);

	const rowStartNum = React.useMemo(() => {
		const page = answerScoreListConditionsToSearch.page;
		const rowsPerPage = answerScoreListConditionsToSearch.rowsPerPage;

		return page * rowsPerPage + 1;
	}, [answerScoreListConditionsToSearch.page, answerScoreListConditionsToSearch.rowsPerPage]);

	const incompleteGradingCount = React.useMemo(() => {
		const totalCount = answerScoreListConditionsToSearch.totalCount;
		const checkCount = answerScoreListConditionsToSearch.checkCount;
		if (checkCount > totalCount) return 0;

		return totalCount - checkCount;
	}, [answerScoreListConditionsToSearch.checkCount, answerScoreListConditionsToSearch.totalCount]);

	const numOfBulkScoreTargets = useMemo(() => {
		const numOfNonTargets = answerScoreList.filter(
			(answerScore) => (answerScore.state === ProjectStateType.EXPORTED) || (answerScore.state === ProjectStateType.SCORED)
		).length;
	
		const numOfTargets = answerScoreList.length - numOfNonTargets;

		return {
			numOfTargets
		};
	  }, [answerScoreList]);

	const handleClickSort = (columnName) => {
		const sortState = sortStateMap[columnName];
		if (sortState) {
			sortState.sort = !sortState.sort;
			setSortStateMap({
				...sortStateMap,
				[columnName]: sortState,
			});

			projectSubmissionAnswerStore.changeSortingHint(columnName, sortState.sort);
		}
	};

	const handleClickPage = (e, value) => {
		const page = value - 1;
		projectSubmissionAnswerStore.changeAnswerScoreListConditionsToSearchPage(page);
	};

	const handleChangeSearchKeyword = (e) => {
		const value = e.target.value;
		setSearchKeyword(value);
		projectSubmissionAnswerStore.changeAnswerScoreListConditionsToSearchKeyword(value.trim());
	};

	const doSearchKeyword = () => {
		initOnTheAiCheckInterval();
		projectSubmissionAnswerStore.getAnswerScoreList(projectCode);
	}

	const handleKeyDownInSearchInput = (e) => {
		if (e.keyCode === 13) {
			e.preventDefault();
			doSearchKeyword();
		}
	};

	const handleClickSearchKeyword = () => {
		doSearchKeyword();
	};

	const handleChangeOnlyScoredCheck = (e) => {
		const checked = e.target.checked;

		// console.log("handleChangeOnlyScoredCheck checked", checked);
		initOnTheAiCheckInterval();
		projectSubmissionAnswerStore.changeAnswerScoreListConditionsToSearchExcept(checked ? ProjectStateType.COMPARED : "");
		projectSubmissionAnswerStore.changeAnswerScoreListConditionsToSearchStateType(ProjectStateType.COMPARED);
		projectSubmissionAnswerStore.getAnswerScoreList(projectCode);
		setCheckedOnlyScored(checked);
	};

	// 일괄 채점 버튼 클릭시 최대 5개씩 묶어서 전송 서버측 async 요청 커넥션 full 방지
	const handleClickBulkMark = async () => {
		setIsBulkScoreLoading(true);
		try {
			// answerScoreList에서 모든 submissionCode 추출
			const submissionCodes = answerScoreList.map(item => item.submissionCode);
			if (submissionCodes.length === 0 || numOfBulkScoreTargets.numOfTargets <= 0) {
				messageStore.alert(i18next.t('채점 대상이 없습니다.'));
				return;
			}

			// 서버에 요청 보내기
			const answerCodesMap = await projectSubmissionAnswerStore.getAnswerCodesBySubmissionCodes(submissionCodes);
			console.log('Retrieved answerCodes:', answerCodesMap);

			// 모든 answerCodes를 하나의 배열로 평탄화
			const allAnswerCodes: string[] = Object.values(answerCodesMap).flat() as string[];
			if (allAnswerCodes.length === 0) {
				messageStore.alert(i18next.t('채점할 답안이 없습니다.'));
				return;
			}

			// 배치 크기 정의
			const BATCH_SIZE = 5;

			// 진행 상황 표시
			let processedCount = 0;
			let totalCount = allAnswerCodes.length;

			// 5개씩 그룹화하여 처리
			for (let i = 0; i < allAnswerCodes.length; i += BATCH_SIZE) {
				// 현재 배치 추출
				const batch = allAnswerCodes.slice(i, i + BATCH_SIZE);
				console.log(`처리 중인 배치 ${Math.floor(i/BATCH_SIZE) + 1}:`, batch);

				try {
					// AI 채점 요청 (한국어로 설정)
					await projectSubmissionAnswerStore.createAIScores(batch, 'ko', true);
					processedCount += batch.length;
					console.log(`진행 상황: ${processedCount}/${totalCount}`);
				} catch (error) {
					console.error(`배치 처리 중 오류 발생 ${Math.floor(i/BATCH_SIZE) + 1}:`, error);
					// 배치 처리 중 오류가 발생해도 계속 진행
				}
			}
			projectSubmissionAnswerStore.getAnswerScoreList(projectCode);
		} catch (error) {
			console.error('일괄 채점 중 오류 발생:', error);
			messageStore.alert(i18next.t('일괄 채점 처리 중 오류가 발생했습니다.'));
		} finally {
			setIsBulkScoreLoading(false);
		}
	};

	const handleClickViewProjectBtn = () => {
		naviCustom.go(`/projects/${projectCode}/create/view`);
		// props.navigate(`/projects/${projectCode}/create/view`);
	};

	const handleOpenMarkDialog = (submissionCode) => {
		setMarkSubmissionCode(submissionCode);
		setOpenMarkDiolog(true);
	};

	const handleCloseMarkDialog = () => {
		setMarkSubmissionCode("");
		setOpenMarkDiolog(false);

		projectSubmissionAnswerStore.getAnswerScoreList(projectSubmissionAnswerStore.projectCode);

		if (searchParams.get('submissionCode')) {
			naviCustom.go(`/assignments/${projectCode}/feedbacks`);
			// props.navigate(`/assignments/${projectCode}/feedbacks`);
		}
	};

	const handleChangeSubmissionCode = (code) => {
		setMarkSubmissionCode(code);
	};

	return (
		<div className={classes.root}>
			<Box
				component="main"
				sx={{
					flexGrow: 1,
					background: '#F2F7F3',
					height: 'calc(100vh - 86px)'
				}}
				display='flex' alignItems='center' justifyContent='center'
				className={classes.padding}
			>
				<Box className={classes.mainContentBox}>
					<Box>
						<Typography className={classes.commonSubTitle}>{i18next.t('채점 및 피드백')}</Typography>
						<Typography className={classes.commonSubmit}>{i18next.t('채점 대상')}(<span>{answerScoreListConditionsToSearch.totalCount}</span>)</Typography>
						<Typography className={classes.submitSubText}>{i18next.t('채점 대상에 해당되는 학생 목록입니다. 자동 채점된 제출물에 점수 확인 및 피드백을 남겨주세요.')}</Typography>
					</Box>
					<Box display='flex' alignItems='center' justifyContent='space-between' sx={{ marginTop: "25px" }}>
						<Box display='flex' alignItems='center' justifyContent='space-between' >
							<Box sx={{ marginRight: "20px" }}>
								<FormControlLabel
									control={
										<Checkbox
											// checked={isElementarySchoolChecked}
											onChange={handleChangeOnlyScoredCheck}
											icon={<CheckCircleIconOff />}
											checkedIcon={<CheckCircleIcon className={classes.checkedColor} />}
											disableRipple
										/>
									}
									label={i18next.t('채점 마감만')}
									className={classes.checkMargin}
								/>
							</Box>
							
							<Paper
								component="form"
								sx={{ p: '2px 4px', display: 'flex', alignItems: 'center' }}
								className={classes.urlInputBox}
							>
								<IconButton className={classes.iconBtnReset} onClick={handleClickSearchKeyword} disableRipple>
									<SearchNameIcon />
								</IconButton>
								<InputBase
									sx={{ ml: 1, flex: 1 }}
									placeholder={i18next.t('이름 검색')}
									value={searchKeyword}
									onChange={handleChangeSearchKeyword}
									onKeyDown={handleKeyDownInSearchInput}
									inputProps={{ 'aria-label': i18next.t('이름 검색') }}
								/>
							</Paper>
						</Box>
						
						<Box display='flex' alignItems='center' justifyContent='space-between' className={classes.submitScanText}>
							<Button onClick={handleClickBulkMark} style={{ marginRight: "10px" }}>일괄채점</Button>
							<Typography>{i18next.t('채점 마감')} <span>{answerScoreListConditionsToSearch.checkCount}</span></Typography>
						</Box>
					</Box>
					<Box>
						<TableContainer component={Paper} className={classes.tableBox}>
							<Table>
								<TableHead>
									<TableRow>
										<TableCell width='6%' align="center">{i18next.t('No.')}</TableCell>
										<TableCell width='12%' >{i18next.t('이름')}</TableCell>
										<TableCell width='12%' >{i18next.t('학년/반')}</TableCell>
										<TableCell width='12%'>{i18next.t('채점 점수')}</TableCell>
										<TableCell width='20%'>
											<Box className={classes.sortButton} onClick={() => handleClickSort(SortingHintColumn.SCORED_DATE_TIME)}>
												<Typography style={{ fontWeight: 800 }}>{i18next.t('채점일시')}</Typography>
												<IconButton className={classes.iconBtnReset} style={{ transform: sortStateMap[SortingHintColumn.SCORED_DATE_TIME].sort ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s ease' }} disableRipple>
													<TableListOrder />
												</IconButton>
											</Box>
										</TableCell>
										<TableCell width='20%'>{i18next.t('채점 진행율')}</TableCell>
										<TableCell width='12%'>{i18next.t('채점')}</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{answerScoreList.length > 0 &&
										answerScoreList.map((item, idx) =>
											<Row
												key={idx}
												idx={idx}
												item={item}
												rowStartNum={rowStartNum}
												projectCode={projectCode}
												handleOpenMarkDialog={handleOpenMarkDialog}
											/>
										)}
								</TableBody>
							</Table>
						</TableContainer>
					</Box>

					{answerScoreList.length === 0 &&
						<Box className={classes.emptyTableData} display='flex' flexDirection='column' alignItems='center' justifyContent='center' >
							<Box display='flex' flexDirection='column' alignItems='center' justifyContent='center' sx={{ height: '500px' }}>
								<EmptyUserBg />
								<Typography className={classes.emptyBgTitle}>{i18next.t('대상이 없습니다.')}</Typography>
								{/*<Typography className={classes.emptyBgTitle}>출제한 대상이 없습니다.</Typography>*/}
								{/*<Typography className={classes.emptyBgText}>'과제 보기' 메뉴에서 생성한 과제 확인 후 대상을 추가하여 출제해 보세요.</Typography>*/}
								{/*<Box>*/}
								{/*    <Button className={classes.emptyViewBtn} onClick={handleClickViewProjectBtn} disableRipple>*/}
								{/*        <Typography>과제 보기</Typography>*/}
								{/*    </Button>*/}
								{/*</Box>*/}
							</Box>
						</Box>
					}

					<Box className={classes.pageNumber} display='flex' alignItems='center' justifyContent='center'>
						<Stack spacing={2}>
							<Pagination
								count={currentAnswerScoreListConditionsToSearch}
								boundaryCount={2}
								showFirstButton
								showLastButton
								onChange={handleClickPage}
								variant="outlined"
							/>
						</Stack>
					</Box>
				</Box>
			</Box>

			{openMarkDiolog &&
				<Dialog
					// className={classes.centerDialog}
					fullScreen open={openMarkDiolog}>
					<MarkFeedback
						projectCode={projectSubmissionAnswerStore.projectCode}
						submissionCode={markSubmissionCode}
						handleClickClose={handleCloseMarkDialog}
						handleChangeSubmissionCode={handleChangeSubmissionCode}
					/>
				</Dialog>
			}

			{projectSubmissionAnswerStore.isSelectingAnswerScoreList &&
				<Backdrop
					sx={{ color: '#fff', zIndex: (theme) => (theme as Theme).zIndex.modal + 1 }}
					open={projectSubmissionAnswerStore.isSelectingAnswerScoreList}
				>
					<CircularProgress color="inherit" />
					<Typography style={{ color: '#fff' }}>&nbsp; {i18next.t('조회 중입니다...')}</Typography>
				</Backdrop>
			}

			<Backdrop
				sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
				open={isBulkScoreLoading}
			>
				<CircularProgress color="inherit" />
			</Backdrop>
		</div>
	);
}

export default observer<typeof MarkFeedbackList>(MarkFeedbackList);
